import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { Icon, type IconName } from '@eliadmin/core/src/components/dlz/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/*
 * The one button of the site – Figma component set "Button" (Styleguide) plus the hand-built variants
 * of the design. Core components use the same file, so their variant names are mapped onto the DLZ look.
 *
 *   variant  primary | secondary | success | white      (Figma Type)
 *   size     l (52 px, DLZ/UI/Button L) | m (40 px, DLZ/UI/Button M)
 *   shape    pill (radius/full) | rounded (18 px – "Do košíka" and "Všetky produkty" in the design)
 */
const PRIMARY =
  'bg-[#D7191F] text-white hover:bg-[#B5141A] disabled:bg-[#ECEDEF] disabled:text-[#9096A0]';
const SECONDARY =
  'border border-[#E2E4E7] bg-white text-[#0F1012] shadow-card-hover hover:bg-[#F5F6F7] disabled:text-[#9096A0]';
const BRAND_SUBTLE = 'bg-[#FDF0F0] text-[#D7191F] hover:bg-[#FBDCDC]';

const buttonVariants = cva(
  [
    'inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2',
    'disabled:pointer-events-none',
    'ea-button',
  ],
  {
    variants: {
      variant: {
        primary: [PRIMARY, 'ea-button--primary'],
        secondary: [SECONDARY, 'ea-button--secondary'],
        success: [
          'bg-[#22A04B] text-white hover:bg-[#0F1012] disabled:bg-[#ECEDEF] disabled:text-[#9096A0]',
          'ea-button--success',
        ],
        /** On photos (hero): white without border and shadow. */
        white: ['bg-white text-[#0F1012] hover:bg-[#F5F6F7]', 'ea-button--white'],

        /* Core variant names */
        default: [PRIMARY, 'ea-button--default'],
        destructive: [PRIMARY, 'ea-button--destructive'],
        accent: [PRIMARY, 'ea-button--accent'],
        outline: [SECONDARY, 'ea-button--outline'],
        ghost: ['text-[#0F1012] hover:bg-[#F5F6F7]', 'ea-button--ghost'],
        link: ['text-[#D7191F] underline-offset-4 hover:underline', 'ea-button--link'],
        dark: ['bg-[#0F1012] text-white hover:bg-[#26292E]', 'ea-button--dark'],
        rating: ['bg-[#FEF6E7] text-[#9A5F00] hover:bg-[#FDEBC8]'],
        'default-alt': [BRAND_SUBTLE],
        'destructive-alt': [BRAND_SUBTLE],
        'accent-alt': [BRAND_SUBTLE],
      },
      size: {
        l: ['dlz-button-l h-[52px] gap-2 px-6', 'ea-button--size-l'],
        m: ['dlz-button-m h-10 gap-2 px-4', 'ea-button--size-m'],

        /* Core size names */
        default: ['dlz-button-l h-[52px] gap-2 px-6', 'ea-button--size-default'],
        lg: ['dlz-button-l h-[52px] gap-2 px-6', 'ea-button--size-lg'],
        sm: ['dlz-button-m h-10 gap-2 px-4', 'ea-button--size-sm'],
        xs: ['h-8 gap-1.5 px-3 text-xs font-semibold', 'ea-button--size-xs'],
        icon: ['size-[52px]', 'ea-button--size-icon'],
        'icon-lg': ['size-[52px]', 'ea-button--size-icon-lg'],
        'icon-sm': ['size-10', 'ea-button--size-icon-sm'],
        'icon-xs': ['size-6', 'ea-button--size-icon-xs'],
      },
      shape: {
        pill: 'rounded-full',
        rounded: 'rounded-[18px]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'l',
      shape: 'pill',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  /** Figma "Icon Left" / "Icon Right" – 24 px in both sizes. */
  iconLeft?: IconName;
  iconRight?: IconName;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      shape,
      asChild = false,
      isLoading,
      iconLeft,
      iconRight,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const blocked = Boolean(props.disabled || isLoading);

    // Keep the original DOM/content in flow: no wrapper may change flex gaps,
    // responsive labels, intrinsic width, or the accessible name during loading.
    const loadingContent = (label: React.ReactNode) => (
      <>
        {iconLeft ? <Icon name={iconLeft} size={24} /> : null}
        <Slottable>{label}</Slottable>
        {iconRight ? <Icon name={iconRight} size={24} /> : null}
        {isLoading && (
          <span
            data-ea-button-spinner=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            style={{ margin: 0 }}
          >
            <Spinner />
          </span>
        )}
      </>
    );
    const content =
      asChild && React.isValidElement<React.HTMLAttributes<HTMLElement>>(children)
        ? React.cloneElement(children, {
            children: loadingContent(children.props.children),
            ...(isLoading && {
              style: { ...children.props.style, WebkitTextFillColor: 'transparent' },
            }),
            // Native disabled does not disable links. Inert blocks pointer and
            // keyboard activation without adding client-only event handlers.
            ...(blocked && {
              tabIndex: -1,
              inert: true,
            }),
          })
        : asChild
          ? children
          : loadingContent(children);
    return (
      <Comp
        className={cn(
          isLoading && 'relative [&>*:not([data-ea-button-spinner])]:opacity-0',
          cn(buttonVariants({ variant, size, shape }), className),
        )}
        ref={ref}
        {...props}
        style={isLoading ? { ...props.style, WebkitTextFillColor: 'transparent' } : props.style}
        disabled={blocked}
        aria-disabled={blocked}
        aria-busy={isLoading || props['aria-busy']}
        data-ea-button-loading={isLoading || undefined}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
